import React from "react";
import axios from "axios";
import { Link, browserHistory } from "react-router";
import { clearMfaSetupContext } from "../utils/mfaSetupContext";
import { clearLoginMfaContext, getLoginMfaContext, setLoginMfaContext, updateLoginMfaContext } from "../utils/loginMfaContext";
import { errorMessages, finalizeLogin, getApiMessage, handleRequiredMfaSetup } from "./loginFlowUtils";

export default class Login extends React.Component {
  constructor(props) {
    super(props);

    const initialMfaContext = getLoginMfaContext();
    this.state = {
      step: initialMfaContext && initialMfaContext.username && initialMfaContext.password ? "token" : "credentials",
      username: (initialMfaContext && initialMfaContext.username) || "",
      password: (initialMfaContext && initialMfaContext.password) || "",
      token: "",
      errorMessage: null,
      tokenError: null,
      usernameError: null,
      passwordError: null,
      mfaRememberForDays: (initialMfaContext && initialMfaContext.mfaRememberForDays) || null,
      rememberMfa: Boolean(initialMfaContext && initialMfaContext.rememberMfa),
      loading: false
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onUseDifferentUser = this.onUseDifferentUser.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
  }

  componentDidMount() {
    if (this.props.authentication && this.props.authentication.user) {
      browserHistory.replace("/events");
      return;
    }

    if (window.location.pathname === "/") {
      browserHistory.replace("/login");
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.authentication.user && this.props.authentication && this.props.authentication.user) {
      browserHistory.replace("/events");
    }
  }

  onInputChange(event) {
    const name = event.target.name;
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;

    this.setState({
      [name]: value,
      errorMessage: null,
      tokenError: name === "token" ? null : this.state.tokenError,
      usernameError: name === "username" ? null : this.state.usernameError,
      passwordError: name === "password" ? null : this.state.passwordError
    });

    if (name === "rememberMfa") {
      updateLoginMfaContext({ rememberMfa: value });
    }
  }

  onUseDifferentUser() {
    clearLoginMfaContext();
    this.setState({
      step: "credentials",
      username: "",
      password: "",
      token: "",
      errorMessage: null,
      tokenError: null,
      usernameError: null,
      passwordError: null,
      mfaRememberForDays: null,
      rememberMfa: false,
      loading: false
    });
  }

  onSubmit(event) {
    event.preventDefault();

    const flowContext = getLoginMfaContext();
    const isCredentialStep = this.state.step === "credentials";
    const loginUsername = isCredentialStep ? this.state.username : (flowContext && flowContext.username) || this.state.username;
    const loginPassword = isCredentialStep ? this.state.password : (flowContext && flowContext.password) || this.state.password;

    if (!loginUsername || !loginPassword) {
      clearLoginMfaContext();
      this.setState({
        errorMessage: "Je inlogsessie is verlopen. Vul je e-mailadres en wachtwoord opnieuw in.",
        step: "credentials",
        mfaRememberForDays: null,
        rememberMfa: false,
        token: ""
      });
      return;
    }

    const payload = {
      username: loginUsername,
      password: loginPassword
    };

    if (this.state.step === "token") {
      payload.token = this.state.token;
      payload.remember_mfa = this.state.rememberMfa;
    }

    if (isCredentialStep) {
      clearLoginMfaContext();
    }
    clearMfaSetupContext();

    this.setState({
      loading: true,
      errorMessage: null,
      tokenError: null,
      usernameError: null,
      passwordError: null
    });

    axios
      .post("/v2/auth/login/", payload, {
        withCredentials: true,
        skipAuthRefresh: true,
        skipAuthToken: true,
        validateStatus: (status) => status >= 200 && status < 500
      })
      .then(async (response) => {
        const data = response.data;
        const status = response.status;

        if (status >= 400 && status < 500) {
          if (handleRequiredMfaSetup({ data: data, redirectTo: "/events" })) {
            clearLoginMfaContext();
            this.setState({ loading: false });
            return;
          }

          if (data && data.code === "token_invalid") {
            this.setState({ loading: false, tokenError: getApiMessage(data, "Dit token is niet geldig. Controleer de code in de app en probeer het opnieuw.") });
            return;
          }

          this.setState({
            loading: false,
            errorMessage: getApiMessage(data),
            usernameError: data && data.username ? data.username : null,
            passwordError: data && data.password ? data.password : null
          });

          if (isCredentialStep) {
            clearLoginMfaContext();
          }
          return;
        }

        if (status === 200) {
          if (data && data.code === "token_required") {
            const rememberForDays = Number(data.mfa_remember_for_days);
            const mfaRememberForDays = Number.isFinite(rememberForDays) && rememberForDays > 0 ? rememberForDays : null;

            setLoginMfaContext({
              username: loginUsername,
              password: loginPassword,
              rememberMfa: false,
              mfaRememberForDays: mfaRememberForDays
            });

            this.setState({
              loading: false,
              step: "token",
              token: "",
              mfaRememberForDays: mfaRememberForDays,
              rememberMfa: false
            });
            return;
          }

          const result = await finalizeLogin({
            data: data,
            setUserData: this.props.setUserData,
            setAuthToken: this.props.setAuthToken,
            redirectTo: "/events"
          });

          if (!result.success) {
            this.setState({
              loading: false,
              errorMessage: errorMessages[result.errorCode] || errorMessages.unknown_error,
              step: "credentials",
              token: "",
              mfaRememberForDays: null,
              rememberMfa: false
            });
            clearLoginMfaContext();
            return;
          }

          clearLoginMfaContext();
          this.setState({ mfaRememberForDays: null, rememberMfa: false });
          return;
        }

        this.setState({ loading: false, errorMessage: errorMessages.unknown_error });
      })
      .catch((error) => {
        this.setState({ loading: false, errorMessage: errorMessages.unknown_error });
        console.warn("Authentication failed", error);
      });
  }

  render() {
    return (
      <div>
        <div className="form form-signin">
          <p className="form-signin-heading">Je kunt hieronder inloggen met jouw e-mailadres en wachtwoord.</p>

          {this.state.errorMessage && <div className="alert alert-danger">{this.state.errorMessage}</div>}

          <form onSubmit={this.onSubmit}>
            {this.state.step === "credentials" && (
              <div>
                <label htmlFor="inputEmail" className="sr-only">
                  E-mailadres
                </label>
                <input
                  id="inputEmail"
                  type="email"
                  name="username"
                  className={"form-control" + (this.state.usernameError ? " is-invalid" : "")}
                  placeholder="E-mailadres"
                  value={this.state.username}
                  onChange={this.onInputChange}
                  required
                />
                {this.state.usernameError && <div className="invalid-feedback d-block">{this.state.usernameError}</div>}

                <label htmlFor="inputPassword" className="sr-only">
                  Wachtwoord
                </label>
                <input
                  id="inputPassword"
                  type="password"
                  name="password"
                  className={"form-control" + (this.state.passwordError ? " is-invalid" : "")}
                  placeholder="Wachtwoord"
                  value={this.state.password}
                  onChange={this.onInputChange}
                  required
                />
                {this.state.passwordError && <div className="invalid-feedback d-block">{this.state.passwordError}</div>}
              </div>
            )}

            {this.state.step === "token" && (
              <div>
                <label htmlFor="inputToken">MFA code</label>
                <input
                  id="inputToken"
                  type="text"
                  name="token"
                  className={"form-control" + (this.state.tokenError ? " is-invalid" : "")}
                  placeholder="123456"
                  value={this.state.token}
                  onChange={this.onInputChange}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                />
                {this.state.tokenError && <div className="invalid-feedback d-block">{this.state.tokenError}</div>}

                {this.state.mfaRememberForDays > 0 && (
                  <div className="form-group mt-2">
                    <label htmlFor="remember-mfa-field">
                      <input id="remember-mfa-field" name="rememberMfa" type="checkbox" checked={this.state.rememberMfa} onChange={this.onInputChange} /> MFA niet opnieuw
                      vragen voor {this.state.mfaRememberForDays} dagen op dit apparaat
                    </label>
                  </div>
                )}

                <div className="form-group mt-2">
                  <Link className="btn btn-link p-0" to="/login/mfa/backup">
                    Gebruik een back-up code
                  </Link>
                </div>
              </div>
            )}

            <button className="btn btn-lg btn-primary btn-block" type="submit" disabled={this.state.loading}>
              {this.state.loading ? "Inloggen..." : "Inloggen"}
            </button>

            {this.state.step === "token" && (
              <button type="button" className="btn btn-light btn-block" onClick={this.onUseDifferentUser}>
                Inloggen met een andere gebruiker
              </button>
            )}

            <div className="password-lost-link">
              <a href="/password-lost">Nog geen wachtwoord of wachtwoord vergeten?</a>
            </div>
            <div className="global-links text-center">versie {JSON.parse(COMMIT_HASH)}</div>
          </form>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./pages/Login.js